import React, { useState, useRef } from 'react';
import { schoolListRequestsAPI } from '../../utils/api';

import { US_STATES } from '../../utils/constants';
import UploadListModal from './UploadListModal';

const GRADES = ['Pre-K','K','1','2','3','4','5','6','7','8','9','10','11','12'];

// --- Sub-components ---

const InputField = ({ label, required, children }) => (
  <div style={{ marginBottom: '1rem' }}>
    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#4b5563', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}{required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
    </label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%', padding: '0.6rem 0.85rem', border: '1.5px solid #e5e7eb', borderRadius: 8,
  fontSize: '0.9rem', color: '#1f2937', background: '#fff', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

const FileUploadZone = ({ file, onFile }) => {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  };

  return (
    <div
      onClick={() => inputRef.current.click()}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      style={{
        border: `2px dashed ${dragging ? '#6366f1' : file ? '#10b981' : '#c7d2fe'}`,
        borderRadius: 12, padding: '1.5rem 1rem', textAlign: 'center', cursor: 'pointer',
        background: dragging ? '#eef2ff' : file ? '#f0fdf4' : '#f8faff',
        transition: 'all 0.2s',
      }}
    >
      <input ref={inputRef} type="file" accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png" style={{ display: 'none' }}
        onChange={e => e.target.files[0] && onFile(e.target.files[0])} />
      {file ? (
        <div>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>📎</div>
          <div style={{ fontWeight: 700, color: '#065f46', fontSize: '0.9rem' }}>{file.name}</div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
            {(file.size / 1024).toFixed(0)} KB · Click to change
          </div>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: '2rem', marginBottom: '0.4rem' }}>☁️</div>
          <div style={{ fontWeight: 600, color: '#4f46e5', fontSize: '0.9rem' }}>Click to upload</div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 3 }}>or drag & drop · PDF, Word, image, text</div>
        </div>
      )}
    </div>
  );
};

// --- Main Component ---

const SchoolNotFound = ({ searchTerm = '' }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1 = school info, 2 = supply list, 3 = success
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    school_name: searchTerm || '',
    school_city: '', school_state: '', grade: '',
    parent_name: '', email: '', phone: '', list_text: '',
  });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleFile = async (f) => {
    if (f.size > 5 * 1024 * 1024) { setError('File must be under 5 MB'); return; }
    setFile(f);
    setError('');
  };

  const goStep2 = () => {
    if (!form.school_name.trim() || !form.parent_name.trim() || !form.email.trim()) {
      setError('School name, your name and email are required'); return;
    }
    setError(''); setStep(2);
  };

  const handleSubmit = async () => {
    setLoading(true); setError('');
    try {
      let file_name = null, file_data = null;
      if (file) {
        file_name = file.name;
        file_data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
      await schoolListRequestsAPI.create({ ...form, file_name, file_data });
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setOpen(false); setStep(1); setFile(null); setError('');
    setForm({ school_name: searchTerm || '', school_city: '', school_state: '', grade: '', parent_name: '', email: '', phone: '', list_text: '' });
  };

  return (
    <>
      {/* Trigger banner */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 14, padding: '1.25rem 1.5rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem',
        boxShadow: '0 4px 20px rgba(99,102,241,0.25)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem' }}>
          <div style={{ fontSize: '2rem' }}>🏫</div>
          <div>
            <div style={{ fontWeight: 800, color: '#fff', fontSize: '1rem' }}>Can't find your school?</div>
            <div style={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.82rem', marginTop: 2 }}>
              Send us your supply list — we'll use AI to instantly build a custom kit for you!
            </div>
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          style={{
            background: '#fff', color: '#4f46e5', fontWeight: 700, fontSize: '0.88rem',
            border: 'none', borderRadius: 8, padding: '0.55rem 1.2rem', cursor: 'pointer',
            whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', transition: 'opacity 0.15s',
          }}
        >
          Upload My List →
        </button>
      </div>

      <UploadListModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default SchoolNotFound;
